import { IAccountTag, IImpersonationContext, ISystemJob } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { UseSelectionStateType } from '../../../../hooks/useSelectionState';
import { useEnvironment, useErrorMessages } from '../../../../models/hooks/appStateHooks';
import {
	invalidateAllTagFilterQueries,
	useBulkDeleteTagsMutation,
	useDeleteTagsMutation,
	useDeleteUnusedTagsMutation,
	useExportTagsMutation,
	useMergeTagsMutation,
	useReevaluateTaggingGameMutation,
} from '../../../../queries/Tag';
import { ConfirmationDialog } from '../../../components/ConfirmationDialog';
import { DeleteConfirmation } from '../../../components/DeleteConfirmation';
import { ExportConfirmationModal } from '../../../components/ExportConfirmation';
import { MergeTagsModal } from '../../../components/reporting/tags/MergeTags';
import { ExportIcon } from '../../../components/svgs/icons/ExportIcon';
import { MergeIcon } from '../../../components/svgs/icons/MergeIcon';
import { RefreshIcon } from '../../../components/svgs/icons/RefreshIcon';
import { TagIcon } from '../../../components/svgs/icons/TagIcon';
import { TrashIcon } from '../../../components/svgs/icons/TrashIcon';
import { Tag } from '../../../components/tags/Tag';
import { header, navigation } from '../../../styles/colors';
import { ActionBarButton } from '../PeopleContainer/PeopleActionsBar/ActionBarButton';
import { useTagsDataContext } from './TagsContexts/TagsDataContext';
import { useTagsSearchContext } from './TagsContexts/TagsSearchContext';
import { getTagSearchFromCriteria } from './TagsUtilities';
import { styleSheet } from './styles';

// Maximum number of tags that can be deleted in a single bulk operation
const MAX_BULK_DELETE = 1000;

interface TagsActionsBarProps {
	impersonationContext?: IImpersonationContext;
	isAdminContext?: boolean;
	selectionState: UseSelectionStateType;
}

export const TagsActionsBar: React.FC<TagsActionsBarProps> = ({
	impersonationContext,
	isAdminContext = false,
	selectionState,
}) => {
	const { tags } = useTagsDataContext();
	const { searchTerm, filter } = useTagsSearchContext();
	const errorMessages = useErrorMessages();
	const { selectedRows: selectedTagIds, isAllSelected, reset: clearSelectedTags } = selectionState;

	// Determine if a search/filter is active
	// Check both searchTerm (local state) and filter.criteria (persisted state)
	const filterSearchValue = getTagSearchFromCriteria(filter?.criteria || []);
	const isSearchActive =
		(!!searchTerm && searchTerm.trim().length > 0) || (!!filterSearchValue && filterSearchValue.trim().length > 0);

	// Determine if any tags are selected
	// When "Select All" is clicked, isAllSelected=true but selectedTagIds=[]
	// So we need to check both conditions
	const hasSelection = selectedTagIds.length > 0 || isAllSelected;

	// Determine if all currently shown tags are selected
	// When search is active, compare against displayed tags (data.length)
	// When no search, compare against total tags (totalCount) to detect "select all" on full list
	const displayedTagCount = tags?.data?.length ?? 0;
	const totalTagCount = tags?.totalCount ?? 0;
	const isAllShownTagsSelected = React.useMemo(
		() =>
			isAllSelected ||
			(isSearchActive
				? displayedTagCount > 0 && selectedTagIds.length === displayedTagCount
				: totalTagCount > 0 && selectedTagIds.length === totalTagCount),
		[isAllSelected, isSearchActive, displayedTagCount, selectedTagIds.length, totalTagCount]
	);

	const invalidateQueries = () => {
		invalidateAllTagFilterQueries();
	};

	const mergeTagsMutation = useMergeTagsMutation({
		impersonationContext,
		onSuccess: () => {
			invalidateQueries();
			clearSelectedTags();
		},
	});

	const [tagsToMerge, setTagsToMerge] = React.useState<IAccountTag[]>([]);

	const [exportTagsPromise, setExportTagsPromise] = React.useState<Promise<ISystemJob>>(null);

	// Modal states
	const [tagsToDelete, setTagsToDelete] = React.useState<IAccountTag[] | null>(null);
	const [isBulkDelete, setIsBulkDelete] = React.useState(false);
	const [bulkDeleteCount, setBulkDeleteCount] = React.useState(0);
	const [showRemoveUnusedConfirmation, setShowRemoveUnusedConfirmation] = React.useState(false);
	const [showReevaluateTaggingGameConfirmation, setShowReevaluateTaggingGameConfirmation] = React.useState(false);

	const deleteTagsMutation = useDeleteTagsMutation({
		impersonationContext,
		onSuccess: () => {
			const count = tagsToDelete?.length || 0;
			toast.success(`${count} tag${count !== 1 ? 's' : ''} successfully deleted`);
			clearSelectedTags();
			invalidateQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	const bulkDeleteTagsMutation = useBulkDeleteTagsMutation({
		impersonationContext,
		onSuccess: () => {
			toast.success(`${bulkDeleteCount} tag${bulkDeleteCount !== 1 ? 's' : ''} successfully deleted`);
			clearSelectedTags();
			invalidateQueries();
			setIsBulkDelete(false);
			setBulkDeleteCount(0);
		},
		onError: error => {
			setIsBulkDelete(false);
			setBulkDeleteCount(0);
			errorMessages.pushApiError(error);
		},
	});

	const exportTagsMutation = useExportTagsMutation({
		onSuccess: () => {
			toast.success('Export initiated. You will receive an email when the export is ready.');
			clearSelectedTags();
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	const deleteUnusedTagsMutation = useDeleteUnusedTagsMutation({
		impersonationContext,
		onSuccess: () => {
			toast.success('Unused tags have been removed.');
			invalidateQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	const reevaluateTaggingGameMutation = useReevaluateTaggingGameMutation({
		impersonationContext,
		onSuccess: () => {
			toast.success('Tag game reevaluation has been initiated.');
			invalidateQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	// Merge: NEVER allow when all shown tags are selected (requires manual selection one by one)
	// Only allow merge when 2+ tags are individually selected
	const canMerge = selectedTagIds.length > 1 && !isAllSelected;

	// Delete: Admin can always delete. Regular users can delete if:
	// - Search is active (even if all filtered results are selected), OR
	// - Not all tags are selected (without search)
	// Both contexts: Cannot be currently deleting
	const canDelete =
		!deleteTagsMutation.isPending &&
		!bulkDeleteTagsMutation.isPending &&
		(isAdminContext ? hasSelection : hasSelection && (isSearchActive || !isAllShownTagsSelected));

	const onDeleteClicked = React.useCallback(() => {
		// Prevent race condition from rapid clicks
		if (deleteTagsMutation.isPending || bulkDeleteTagsMutation.isPending) {
			return;
		}

		// When "Select All" is used, we use the bulk delete API
		if (isAllSelected) {
			const totalCount = tags?.totalCount ?? 0;
			const excludedCount = selectionState.excludedRows.length;
			const effectiveCount = totalCount - excludedCount;

			// Check if total exceeds the MAX_BULK_DELETE tag limit
			if (effectiveCount > MAX_BULK_DELETE) {
				toast.error(
					`For your protection, you cannot delete more than ${MAX_BULK_DELETE} tags. Please contact support@levitateapp.com and they can assist you.`
				);
				return;
			}

			// Set bulk delete mode and show confirmation
			setIsBulkDelete(true);
			setBulkDeleteCount(effectiveCount);
			setTagsToDelete([]); // Empty array to trigger confirmation modal
		} else {
			// Regular delete: individual tags selected
			setIsBulkDelete(false);
			setTagsToDelete(tags?.data?.filter(x => selectedTagIds.includes(x.id)) || []);
		}
	}, [
		tags,
		selectedTagIds,
		isAllSelected,
		selectionState.excludedRows,
		deleteTagsMutation.isPending,
		bulkDeleteTagsMutation.isPending,
	]);

	const onExportTagsButtonClicked = () => {
		const promise = exportTagsMutation.mutateAsync();
		setExportTagsPromise(promise);
	};

	const onExportTagsClose = () => {
		setExportTagsPromise(null);
	};

	const handleMergeTagsClick = () => {
		const selectedTags = tags?.data?.filter(tag => selectedTagIds.includes(tag.id));
		setTagsToMerge(selectedTags);
	};

	const handleMergeTagsModalClose = (targetTag?: IAccountTag, canceled?: boolean) => {
		setTagsToMerge([]);

		if (!canceled && targetTag) {
			// Perform the merge operation
			const selectedTags = tags?.data?.filter(tag => selectedTagIds.includes(tag.id));
			const sourceTagValues = selectedTags.filter(tag => tag.id !== targetTag.id).map(tag => tag.name);

			if (sourceTagValues.length > 0) {
				mergeTagsMutation.mutate({
					sourceTagValues,
					targetTagValue: targetTag.name,
				});
			}
		}
		clearSelectedTags();
	};

	const handleDeleteConfirmationFinished = (shouldDelete: boolean) => {
		if (shouldDelete) {
			if (isBulkDelete) {
				// Perform bulk delete using new API
				const query = isSearchActive ? filterSearchValue || searchTerm || null : null;
				bulkDeleteTagsMutation.mutate({
					query,
					excludeTagIds: selectionState.excludedRows,
				});
			} else if (tagsToDelete && tagsToDelete.length > 0) {
				// Perform regular delete
				const tagNames = tagsToDelete.map(tag => tag.name);
				deleteTagsMutation.mutate(tagNames);
			}
		}

		setTagsToDelete(null);
		setIsBulkDelete(false);
		setBulkDeleteCount(0);
	};

	const handleRemoveUnusedTagsClick = () => {
		setShowRemoveUnusedConfirmation(true);
	};

	const handleRemoveUnusedConfirmationClose = (result: { isCancel: boolean }) => {
		setShowRemoveUnusedConfirmation(false);

		if (!result.isCancel) {
			deleteUnusedTagsMutation.mutate();
		}
	};

	const handleReevaluateTaggingGameClick = () => {
		setShowReevaluateTaggingGameConfirmation(true);
	};

	const handleReevaluateTaggingGameConfirmationClose = (shouldContinue: { isCancel: boolean }) => {
		setShowReevaluateTaggingGameConfirmation(false);

		if (!shouldContinue.isCancel) {
			reevaluateTaggingGameMutation.mutate();
		}
	};

	const isAdminView = useEnvironment()?.appType === 'admin';
	const shouldShowExport = !isAdminView && !impersonationContext;

	const renderDeleteConfirmationBody = () => {
		if (!tagsToDelete) return null;

		// Handle bulk delete mode
		if (isBulkDelete) {
			const bulkCount = bulkDeleteCount;
			const bulkShowCount = bulkCount > 10;

			// Build recovery warning with proper formatting
			const bulkRecoveryWarning =
				isAdminContext && bulkShowCount
					? 'Once these tags are deleted, they cannot be recovered, and it will be ALL YOUR FAULT.'
					: 'Once these tags are deleted, they cannot be recovered.';

			// Build the final confirmation message
			const bulkConfirmationMessage = `By deleting these tags, you will also delete any notifications you've set for contacts that have these tags. Are you sure you want to delete these tags?`;

			return (
				<div>
					<p>
						You are about to delete <strong>{bulkCount} tags</strong>.
					</p>
					<p>
						<em>
							Note: Tags associated with integrations or automations will be automatically excluded and cannot be
							deleted.
						</em>
					</p>
					<p>
						<strong>{bulkRecoveryWarning}</strong>
					</p>
					<p>{bulkConfirmationMessage}</p>
				</div>
			);
		}

		// Handle regular delete mode
		const count = tagsToDelete.length;
		const isPlural = count > 1;
		const tagCountText = isPlural ? 'these tags' : 'this tag';
		const showCount = count > 10;

		// Check for integration-sourced tags
		const tagsFromIntegration = tagsToDelete.filter(tag => {
			const source = tag.source ? tag.source.toLowerCase() : null;
			return source === 'hawksoft' || source === 'ams360' || source === 'redtail' || source === 'eclipse';
		});

		// Build integration warning if applicable
		let integrationWarning = null;
		if (tagsFromIntegration.length) {
			const warningPrefix =
				tagsFromIntegration.length === 1 && count === 1
					? 'This tag is'
					: tagsFromIntegration.length === count
						? 'These tags are'
						: 'Some of these tags are';
			integrationWarning = `${warningPrefix} associated with an integration and may come back.`;
		}

		// Build recovery warning with proper formatting
		const recoveryWarning =
			isAdminContext && showCount
				? 'Once these tags are deleted, they cannot be recovered, and it will be ALL YOUR FAULT.'
				: isPlural
					? 'Once these tags are deleted, they cannot be recovered.'
					: 'Once this tag is deleted, it cannot be recovered.';

		// Build the final confirmation message
		const confirmationMessage = `By deleting ${tagCountText}, you will also delete any notifications you've set for contacts that have ${tagCountText}. Are you sure you want to delete ${tagCountText}?`;

		return (
			<div>
				{showCount ? (
					<p>
						You are about to delete <strong>{count} tags</strong>.
					</p>
				) : (
					<div className={css(styleSheet.deleteConfirmationTags)}>
						<div className={css(styleSheet.deleteConfirmationTagsContent)}>
							{tagsToDelete.map((x, i) => {
								return <Tag className={css(styleSheet.deleteConfirmationTag)} key={`${x.id}-${i}`} tagValue={x.name} />;
							})}
						</div>
					</div>
				)}
				{integrationWarning && <p>{integrationWarning}</p>}
				<p>
					<strong>{recoveryWarning}</strong>
				</p>
				<p>{confirmationMessage}</p>
			</div>
		);
	};

	return (
		<div className={css(styleSheet.container)}>
			<ActionBarButton
				actionButtonType='standard'
				key='merge-tags'
				icon={<MergeIcon />}
				onClick={handleMergeTagsClick}
				disabled={!canMerge}
				isFullWidth={true}
				styleClass={styleSheet.mergeTags}
			>
				Merge
			</ActionBarButton>
			<ActionBarButton
				actionButtonType='standard'
				key='delete-tags'
				icon={<TrashIcon fillColor={header} />}
				onClick={onDeleteClicked}
				disabled={!canDelete}
				isFullWidth={true}
				styleClass={styleSheet.deleteTags}
			>
				Delete
			</ActionBarButton>
			{shouldShowExport && (
				<ActionBarButton
					actionButtonType='standard'
					key='export-tags'
					icon={<ExportIcon />}
					onClick={onExportTagsButtonClicked}
					isFullWidth={true}
					disabled={tags?.data?.length === 0}
					styleClass={styleSheet.exportTags}
				>
					Export
				</ActionBarButton>
			)}

			{isAdminContext && (
				<>
					<ActionBarButton
						actionButtonType='standard'
						key='remove-unused-tags'
						onClick={handleRemoveUnusedTagsClick}
						isFullWidth={true}
						icon={<TagIcon fillColor={navigation} />}
						styleClass={styleSheet.removeUnusedTags}
					>
						Remove Unused Tags
					</ActionBarButton>
					<ActionBarButton
						actionButtonType='standard'
						key='reevaluate-tagging-game'
						onClick={handleReevaluateTaggingGameClick}
						isFullWidth={true}
						icon={<RefreshIcon height={15} width={15} />}
						styleClass={styleSheet.reevaluateTaggingGame}
					>
						Re-evaluate Tagging Game
					</ActionBarButton>
				</>
			)}

			<ExportConfirmationModal
				promise={exportTagsPromise}
				modalProps={{
					isOpen: !!exportTagsPromise,
					onRequestClose: onExportTagsClose,
				}}
				subTitle={
					<>
						<p>The fields we&apos;ll export include: Company Name, Company, Job Title,</p>
						<p>Address, Phone Numbers, Email Address, OwnerID, and tags.</p>
					</>
				}
			/>
			<MergeTagsModal isOpen={!!tagsToMerge?.length} onRequestClose={handleMergeTagsModalClose} tags={tagsToMerge} />
			<DeleteConfirmation
				bodyText={renderDeleteConfirmationBody()}
				isOpen={isBulkDelete || (!!tagsToDelete && tagsToDelete.length > 0)}
				onFinish={handleDeleteConfirmationFinished}
			/>

			<ConfirmationDialog
				modalProps={{
					isOpen: showRemoveUnusedConfirmation,
					onRequestClose: handleRemoveUnusedConfirmationClose,
				}}
				title='Remove Unused Tags'
				options={[
					{
						title: 'Cancel',
						isCancel: true,
					},
					{
						title: 'Remove',
						isCta: true,
					},
				]}
			>
				<div>
					<p>This will permanently remove all tags not assigned to any contacts.</p>
					<p>Are you sure you want to continue?</p>
				</div>
			</ConfirmationDialog>
			<ConfirmationDialog
				title='Reevaluate Tagging Game'
				options={[
					{
						title: 'Cancel',
						isCancel: true,
					},
					{
						title: 'Reevaluate',
						isCta: true,
					},
				]}
				modalProps={{
					isOpen: showReevaluateTaggingGameConfirmation,
					onRequestClose: handleReevaluateTaggingGameConfirmationClose,
				}}
			>
				<div>
					<p>This will reevaluate tagging game suggestions for all contacts.</p>
					<p>This operation may take a while to complete.</p>
					<p>Are you sure you want to continue?</p>
				</div>
			</ConfirmationDialog>
		</div>
	);
};
